<script setup>
/* global Sentry */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { rotaDoCard } from 'dashboard/helper/rotaDoCard';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import PipelinesAPI from 'dashboard/api/pipelines';
import AdvupSelect from 'dashboard/components-next/AdvupSelect.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import EsqueletoLista from 'dashboard/components-next/EsqueletoLista.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import LostReasonModal from 'dashboard/routes/dashboard/pipeline/components/LostReasonModal.vue';
import WonReasonModal from 'dashboard/routes/dashboard/pipeline/components/WonReasonModal.vue';

// slate-9 da paleta advup. Hexadecimal por extenso porque a cor da etapa vem
// do banco (a que o escritório escolheu) e segue para uma variável CSS — não
// há classe para cair.
const CINZA_DA_ETAPA = '#8b8d98';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');

const pipelineCard = ref(null);
const pipelines = ref([]);
const loading = ref(false);
const falhou = ref(false);
const showAddForm = ref(false);
const selectedPipelineId = ref(null);
const selectedStage = ref(null);
const creatingCard = ref(false);
const removendo = ref(false);
const confirmarRemocao = ref(null);

// Na loja do painel, `id` É o display_id (as conversas são indexadas por ele).
const conversationId = computed(() => currentChat.value?.id || null);
const contact = computed(() => currentChat.value?.meta?.sender || {});

const avisarSentry = (error, contexto) => {
  if (!window.Sentry) return;
  Sentry.withScope(scope => {
    if (contexto) scope.setContext('pipeline_card', contexto);
    scope.setExtra('error_response', error?.response?.data);
    Sentry.captureException(error);
  });
};

// As etapas na ORDEM DO FUNIL. Eram listadas na ordem do objeto que vem do
// banco, que não é a ordem em que o escritório arrumou as colunas.
const etapasEmOrdem = pipeline =>
  Object.entries(pipeline?.stages || {})
    .map(([chave, etapa]) => ({ chave, ...etapa }))
    .sort((a, b) => (a.position || 0) - (b.position || 0));

const currentPipeline = computed(() =>
  pipelines.value.find(f => f.id === pipelineCard.value?.pipeline_id)
);
const selectedPipeline = computed(() =>
  pipelines.value.find(f => f.id === selectedPipelineId.value)
);

const etapasDoCard = computed(() => etapasEmOrdem(currentPipeline.value));
const etapaAtual = computed(() =>
  etapasDoCard.value.find(e => e.chave === pipelineCard.value?.pipeline_stage)
);
const corDaEtapa = computed(() => etapaAtual.value?.color || CINZA_DA_ETAPA);

// A trilha mostra o caminho de trabalho — ganho e perdido são desfechos, não
// passos, e ficariam como "o fim do caminho" de qualquer card.
const trilha = computed(() =>
  etapasDoCard.value.filter(e => !e.is_won_stage && !e.is_lost_stage)
);
const passoAtual = computed(() =>
  trilha.value.findIndex(e => e.chave === pipelineCard.value?.pipeline_stage)
);

const opcoesDeEtapa = pipeline =>
  etapasEmOrdem(pipeline).map(e => ({
    label: e.name,
    value: e.chave,
    color: e.color || CINZA_DA_ETAPA,
  }));

const opcoesDeFunil = computed(() =>
  pipelines.value.map(p => ({ label: p.name, value: p.id }))
);

const primeiraEtapa = pipeline => {
  const etapas = etapasEmOrdem(pipeline);
  return (
    etapas.find(e => !e.is_won_stage && !e.is_lost_stage)?.chave ||
    etapas[0]?.chave ||
    'lead'
  );
};

// A conversa pode mudar enquanto a busca anda: só grava a resposta da
// conversa que ainda está aberta.
const fetchPipelineData = async () => {
  const pedida = conversationId.value;
  if (!pedida) return;
  loading.value = true;
  falhou.value = false;
  showAddForm.value = false;
  try {
    const [funis, cards] = await Promise.all([
      PipelinesAPI.get(),
      PipelineCardsAPI.getCardByConversationId(pedida),
    ]);
    if (pedida !== conversationId.value) return;
    pipelines.value = funis.data || [];
    pipelineCard.value = cards.data?.[0] || null;
  } catch (error) {
    if (pedida !== conversationId.value) return;
    // 🔴 Falhar parecia "esta conversa não está em nenhum pipeline" — com o
    // botão de adicionar à vista, o que criava o card em dobro.
    falhou.value = true;
    avisarSentry(error);
  } finally {
    if (pedida === conversationId.value) loading.value = false;
  }
};

// Uma busca só ao abrir: havia o observador imediato E o `mounted`, e as duas
// buscas saíam juntas.
watch(conversationId, fetchPipelineData, { immediate: true });

// Só invalida o quadro guardado; se falhar, o quadro recarrega na próxima
// visita — não é motivo para dizer à pessoa que o card não mudou.
const avisarQuadro = () =>
  store.dispatch('pipelineCards/itemUpdated').catch(() => {});

const goToPipeline = () => {
  if (!pipelineCard.value || !currentPipeline.value) return;
  router.push(
    rotaDoCard({
      accountId: route.params.accountId,
      funil: currentPipeline.value,
      itemId: pipelineCard.value.id,
    })
  );
};

const gravarEtapa = async (novaEtapa, extras = {}) => {
  const card = pipelineCard.value;
  try {
    pipelineCard.value = await PipelineCardsAPI.moveToStage(
      card.id,
      novaEtapa,
      card.pipeline_stage,
      extras
    );
    avisarQuadro();
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.MOVED_SUCCESS'));
  } catch (error) {
    // O seletor lê o card: sem gravar nada, ele volta sozinho para a etapa
    // em que o card continua.
    avisarSentry(error);
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.MOVED_ERROR'));
  }
};

// 🔴 GANHO E PERDIDO PEDEM O PORQUÊ, COMO NO QUADRO (16/09/2026).
// No quadro, soltar em "Perdido" pede o motivo e em "Ganho" o valor. Daqui o
// card ia direto, e o relatório de perdas ficava sem motivo. Agora abrem as
// MESMAS janelas do quadro.
const etapaPendente = ref(null);
const moveToStage = async novaEtapa => {
  const card = pipelineCard.value;
  if (!card || novaEtapa === card.pipeline_stage) return;
  const etapa = etapasDoCard.value.find(e => e.chave === novaEtapa);
  if (etapa?.is_lost_stage || etapa?.is_won_stage) {
    etapaPendente.value = {
      chave: novaEtapa,
      tipo: etapa.is_lost_stage ? 'perdido' : 'ganho',
    };
    return;
  }
  await gravarEtapa(novaEtapa);
};

const confirmarPerda = async motivo => {
  const pendente = etapaPendente.value;
  etapaPendente.value = null;
  if (pendente) await gravarEtapa(pendente.chave, { lostReason: motivo });
};

const confirmarGanho = async ({ wonValue, wonNote }) => {
  const pendente = etapaPendente.value;
  etapaPendente.value = null;
  if (pendente) await gravarEtapa(pendente.chave, { wonValue, wonNote });
};

const tituloDoCard = computed(
  () =>
    pipelineCard.value?.item_details?.title ||
    pipelineCard.value?.title ||
    contact.value?.name ||
    ''
);

const changePipeline = async novoId => {
  const card = pipelineCard.value;
  if (!card || novoId === card.pipeline_id) return;
  const novo = pipelines.value.find(p => p.id === novoId);
  if (!novo) return;
  const etapa = primeiraEtapa(novo);
  try {
    await PipelineCardsAPI.updateCard(card.id, {
      pipeline_id: novoId,
      pipeline_stage: etapa,
    });
    pipelineCard.value = {
      ...card,
      pipeline_id: novoId,
      pipeline_stage: etapa,
    };
    avisarQuadro();
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.PIPELINE_CHANGED_SUCCESS'));
  } catch (error) {
    avisarSentry(error);
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.PIPELINE_CHANGED_ERROR'));
  }
};

const removeFromPipeline = async () => {
  const card = pipelineCard.value;
  if (!card || removendo.value) return;
  removendo.value = true;
  try {
    await PipelineCardsAPI.deleteCard(card.id, { currentCard: card });
    pipelineCard.value = null;
    confirmarRemocao.value?.close();
    avisarQuadro();
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.REMOVED_SUCCESS'));
  } catch (error) {
    avisarSentry(error);
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.REMOVED_ERROR'));
  } finally {
    removendo.value = false;
  }
};

const abrirForm = () => {
  const primeiro = pipelines.value[0];
  if (!primeiro) return;
  selectedPipelineId.value = primeiro.id;
  selectedStage.value = primeiraEtapa(primeiro);
  showAddForm.value = true;
};

const escolherFunil = id => {
  selectedPipelineId.value = id;
  selectedStage.value = primeiraEtapa(selectedPipeline.value);
};

const createPipelineCard = async () => {
  const conversa = conversationId.value;
  if (
    !selectedPipelineId.value ||
    !selectedStage.value ||
    !conversa ||
    creatingCard.value
  )
    return;
  creatingCard.value = true;
  try {
    const { data } = await PipelineCardsAPI.createCard({
      pipeline_id: selectedPipelineId.value,
      pipeline_stage: selectedStage.value,
      conversation_display_id: conversa,
      item_details: {
        title:
          contact.value?.name ||
          t('CONTACT_PANEL.PIPELINE_SECTION.NEW_CARD_TITLE'),
        conversation_id: conversa,
      },
    });
    pipelineCard.value = data;
    showAddForm.value = false;
    avisarQuadro();
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.ADDED_SUCCESS'));
  } catch (error) {
    avisarSentry(error, {
      pipeline_id: selectedPipelineId.value,
      stage: selectedStage.value,
      conversation_id: conversa,
    });
    useAlert(t('CONTACT_PANEL.PIPELINE_SECTION.ADDED_ERROR'));
  } finally {
    creatingCard.value = false;
  }
};
</script>

<template>
  <div class="pls">
    <EsqueletoLista
      v-if="loading"
      :linhas="2"
      :altura="44"
      :rotulo="t('CONTACT_PANEL.PIPELINE_SECTION.LOADING')"
    />

    <EstadoVazio
      v-else-if="falhou"
      class="pls-vazio"
      tom="erro"
      icone="i-lucide-wifi-off"
      :titulo="t('CONTACT_PANEL.PIPELINE_SECTION.LOAD_ERROR')"
    >
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="fetchPipelineData"
      >
        <i class="i-lucide-rotate-cw" aria-hidden="true" />
        {{ t('CONTACT_PANEL.PIPELINE_SECTION.RETRY') }}
      </button>
    </EstadoVazio>

    <template v-else-if="pipelineCard">
      <!-- O cartão do funil: a cor da etapa atual (a que o escritório
           escolheu) pinta o ponto, o fio e o caminho andado. -->
      <div class="advup-card pls-cartao" :style="{ '--etapa': corDaEtapa }">
        <div class="pls-cartao__topo">
          <span class="pls-cartao__selo" aria-hidden="true">
            <i class="i-lucide-kanban" />
          </span>
          <div class="pls-cartao__txt">
            <p class="pls-cartao__funil">
              {{
                currentPipeline?.name ||
                t('CONTACT_PANEL.PIPELINE_SECTION.NO_PIPELINE')
              }}
            </p>
            <p v-if="etapaAtual" class="pls-cartao__etapa">
              <span class="pls-ponto" aria-hidden="true" />
              {{ etapaAtual.name }}
            </p>
          </div>
          <button
            v-tooltip.top="t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
            type="button"
            class="advup-acao advup-acao--p advup-acao--perigo"
            :aria-label="t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
            @click="confirmarRemocao?.open()"
          >
            <i class="i-lucide-trash-2" />
          </button>
        </div>

        <ol
          v-if="trilha.length > 1 && passoAtual >= 0"
          class="pls-trilha"
          :aria-label="
            t('CONTACT_PANEL.PIPELINE_SECTION.PROGRESS', {
              atual: passoAtual + 1,
              total: trilha.length,
            })
          "
        >
          <li
            v-for="(passo, i) in trilha"
            :key="passo.chave"
            v-tooltip.top="passo.name"
            class="pls-trilha__passo"
            :class="{
              'pls-trilha__passo--feito': i <= passoAtual,
              'pls-trilha__passo--atual': i === passoAtual,
            }"
          />
        </ol>
      </div>

      <div v-if="pipelines.length > 1" class="pls-campo">
        <span class="advup-rotulo">{{
          t('CONTACT_PANEL.PIPELINE_SECTION.CHANGE_PIPELINE')
        }}</span>
        <AdvupSelect
          :model-value="pipelineCard.pipeline_id"
          :options="opcoesDeFunil"
          :rotulo="t('CONTACT_PANEL.PIPELINE_SECTION.CHANGE_PIPELINE')"
          min-width="100%"
          @update:model-value="changePipeline"
        />
      </div>

      <div v-if="etapasDoCard.length" class="pls-campo">
        <span class="advup-rotulo">{{
          t('CONTACT_PANEL.PIPELINE_SECTION.MOVE_STAGE')
        }}</span>
        <AdvupSelect
          :model-value="pipelineCard.pipeline_stage"
          :options="opcoesDeEtapa(currentPipeline)"
          :rotulo="t('CONTACT_PANEL.PIPELINE_SECTION.MOVE_STAGE')"
          min-width="100%"
          @update:model-value="moveToStage"
        />
      </div>

      <button
        type="button"
        class="advup-btn advup-btn--compacto pls-ver"
        @click="goToPipeline"
      >
        <i class="i-lucide-external-link" aria-hidden="true" />
        {{ t('CONTACT_PANEL.PIPELINE_SECTION.VIEW_ITEM') }}
      </button>
    </template>

    <form
      v-else-if="showAddForm"
      class="pls-form"
      @submit.prevent="createPipelineCard"
    >
      <div class="pls-campo">
        <span class="advup-rotulo">{{
          t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_PIPELINE')
        }}</span>
        <AdvupSelect
          :model-value="selectedPipelineId"
          :options="opcoesDeFunil"
          :rotulo="t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_PIPELINE')"
          min-width="100%"
          @update:model-value="escolherFunil"
        />
      </div>
      <div v-if="selectedPipeline?.stages" class="pls-campo">
        <span class="advup-rotulo">{{
          t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_STAGE')
        }}</span>
        <AdvupSelect
          v-model="selectedStage"
          :options="opcoesDeEtapa(selectedPipeline)"
          :rotulo="t('CONTACT_PANEL.PIPELINE_SECTION.SELECT_STAGE')"
          min-width="100%"
        />
      </div>
      <div class="pls-form__acoes">
        <button
          type="button"
          class="advup-btn advup-btn--compacto"
          @click="showAddForm = false"
        >
          {{ t('CONTACT_PANEL.PIPELINE_SECTION.CANCEL') }}
        </button>
        <button
          type="submit"
          class="advup-btn-forte advup-btn--compacto"
          :disabled="!selectedPipelineId || !selectedStage || creatingCard"
        >
          <i
            :class="
              creatingCard ? 'i-lucide-loader-2 advup-gira' : 'i-lucide-plus'
            "
            aria-hidden="true"
          />
          {{ t('CONTACT_PANEL.PIPELINE_SECTION.ADD') }}
        </button>
      </div>
    </form>

    <EstadoVazio
      v-else
      class="pls-vazio"
      icone="i-lucide-kanban"
      :titulo="t('CONTACT_PANEL.PIPELINE_SECTION.NO_ITEM')"
      :dica="
        pipelines.length
          ? t('CONTACT_PANEL.PIPELINE_SECTION.ADD_HINT')
          : t('CONTACT_PANEL.PIPELINE_SECTION.NO_PIPELINES_AVAILABLE')
      "
    >
      <button
        v-if="pipelines.length"
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="abrirForm"
      >
        <i class="i-lucide-plus" aria-hidden="true" />
        {{ t('CONTACT_PANEL.PIPELINE_SECTION.ADD_TO_PIPELINE') }}
      </button>
    </EstadoVazio>

    <LostReasonModal
      :show="etapaPendente?.tipo === 'perdido'"
      :card-title="tituloDoCard"
      @confirm="confirmarPerda"
      @cancel="etapaPendente = null"
    />
    <WonReasonModal
      :show="etapaPendente?.tipo === 'ganho'"
      :card-title="tituloDoCard"
      :expected-revenue="pipelineCard?.expected_revenue"
      :currency="pipelineCard?.currency || 'BRL'"
      @confirm="confirmarGanho"
      @cancel="etapaPendente = null"
    />

    <Dialog
      ref="confirmarRemocao"
      type="alert"
      :title="t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE_TITLE')"
      :description="t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE_CONFIRM')"
      :confirm-button-label="t('CONTACT_PANEL.PIPELINE_SECTION.REMOVE')"
      :is-loading="removendo"
      @confirm="removeFromPipeline"
    />
  </div>
</template>

<style scoped>
.pls {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
}

/* O vazio da peça é de tela inteira (48px embaixo). Numa seção da ficha ele
   fica do tamanho do que diz. */
.pls-vazio {
  padding: var(--esp-4) var(--esp-2);
}

.pls-cartao {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  padding: var(--esp-3);
  border-inline-start: 3px solid var(--etapa);
  animation: advupPop var(--mov-3) var(--mov-curva) backwards;
}

.pls-cartao__topo {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
}

.pls-cartao__selo {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: var(--r-3);
  background: color-mix(in srgb, var(--etapa) 14%, transparent);
  color: var(--etapa);
}

.pls-cartao__selo > i {
  display: block;
  font-size: var(--ico-2);
  line-height: 1;
}

.pls-cartao__txt {
  flex: 1;
  min-width: 0;
}

.pls-cartao__funil,
.pls-cartao__etapa {
  overflow: hidden;
  margin: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pls-cartao__funil {
  font-size: var(--txt-nota);
  color: rgb(var(--slate-11));
}

.pls-cartao__etapa {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  font-size: var(--txt-corpo);
  font-weight: var(--peso-forte);
  color: rgb(var(--slate-12));
}

.pls-ponto {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--r-redondo);
  background: var(--etapa);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--etapa) 22%, transparent);
}

/* A TRILHA: um segmento por etapa de trabalho, cheio até a atual. Diz num
   relance "está no começo" ou "está quase fechando", que o nome da etapa
   sozinho não diz. */
.pls-trilha {
  display: flex;
  gap: var(--esp-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.pls-trilha__passo {
  flex: 1;
  height: 4px;
  border-radius: var(--r-redondo);
  background: rgb(var(--slate-4));
  transition: background-color var(--mov-3) var(--mov-curva);
}

.pls-trilha__passo--feito {
  background: var(--etapa);
}

.pls-trilha__passo--atual {
  animation: pls-cresce var(--mov-4) var(--mov-curva-pulo) backwards;
}

@keyframes pls-cresce {
  from {
    transform: scaleX(0.2);
    transform-origin: left;
  }
}

.pls-campo {
  display: flex;
  flex-direction: column;
}

.pls-ver {
  width: 100%;
}

.pls-form {
  display: flex;
  flex-direction: column;
  gap: var(--esp-3);
  animation: advupPop var(--mov-2) var(--mov-curva-pulo) backwards;
}

.pls-form__acoes {
  display: flex;
  gap: var(--esp-2);
}

.pls-form__acoes > * {
  flex: 1;
}

@media (prefers-reduced-motion: reduce) {
  .pls-cartao,
  .pls-form,
  .pls-trilha__passo--atual {
    animation: none;
  }

  .pls-trilha__passo {
    transition: none;
  }
}
</style>
